import React, { useEffect, useState } from "react";
import {
  createTranslation,
  deleteTranslation,
  getTranslations,
  updateTranslation,
} from "../processes/translation/translation";

const CUSTOM_LANGUAGE_OPTION = "__custom__";
const LANGUAGE_OPTIONS = [
  { value: "en", label: "English (en)" },
  { value: "id", label: "Bahasa Indonesia (id)" },
  { value: "zh-CN", label: "Chinese Simplified (zh-CN)" },
];

function isKnownLanguage(code) {
  return LANGUAGE_OPTIONS.some((opt) => opt.value === code);
}

function AutosizeTextarea({
  value,
  onChange,
  className,
  placeholder,
  disabled,
  minRows = 2,
  style,
  ...props
}) {
  const ref = React.useRef(null);
  const lineHeight = 20;

  const resize = React.useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    const minHeight = lineHeight * minRows + 14; // line height + vertical padding/border
    el.style.height = `${Math.max(el.scrollHeight, minHeight)}px`;
  }, [minRows]);

  React.useEffect(() => {
    resize();
  }, [value, resize]);

  return (
    <textarea
      ref={ref}
      className={className}
      value={value}
      onChange={onChange}
      onInput={resize}
      placeholder={placeholder}
      disabled={disabled}
      style={{
        resize: "vertical",
        overflow: "hidden",
        lineHeight: `${lineHeight}px`,
        ...style,
      }}
      {...props}
    />
  );
}

function emptyCreateForm() {
  return {
    translationKey: "",
    languageCode: "en",
    translationText: "",
  };
}

function normalizeRows(rows) {
  return (Array.isArray(rows) ? rows : []).map((row) => ({
    ...row,
    _draftText: row.translationText ?? "",
    _saving: false,
    _deleting: false,
    _error: "",
  }));
}

export default function Translations() {
  const [filters, setFilters] = useState({
    search: "",
    translationKey: "",
    languageCode: "",
  });
  const [createForm, setCreateForm] = useState(emptyCreateForm());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [filterLanguageMode, setFilterLanguageMode] = useState("select");
  const [showCreateForm, setShowCreateForm] = useState(false);

  const loadTranslations = async (nextFilters = filters) => {
    setLoading(true);
    setError("");
    try {
      const res = await getTranslations({
        ...nextFilters,
        limit: 500,
      });
      setRows(normalizeRows(res.data));
      setInfo("");
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Failed to load translations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTranslations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const patchRow = (translationKey, languageCode, patch) => {
    setRows((prev) =>
      prev.map((row) =>
        row.translationKey === translationKey && row.languageCode === languageCode
          ? { ...row, ...patch }
          : row
      )
    );
  };

  const createLanguageSelectValue = isKnownLanguage(createForm.languageCode)
    ? createForm.languageCode
    : CUSTOM_LANGUAGE_OPTION;

  const filterLanguageSelectValue =
    filters.languageCode === ""
      ? ""
      : isKnownLanguage(filters.languageCode)
        ? filters.languageCode
        : CUSTOM_LANGUAGE_OPTION;

  const onCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    setInfo("");
    try {
      const res = await createTranslation(createForm);
      if (!res?.ok) {
        throw new Error(res?.message || "Failed to create translation");
      }
      setInfo(res?.message || "Created");
      setCreateForm(emptyCreateForm());
      await loadTranslations();
    } catch (e2) {
      setError(e2?.response?.data?.message || e2?.message || "Failed to create translation");
    } finally {
      setSubmitting(false);
    }
  };

  const onSaveRow = async (row) => {
    patchRow(row.translationKey, row.languageCode, { _saving: true, _error: "" });
    setInfo("");
    try {
      const res = await updateTranslation({
        translationKey: row.translationKey,
        languageCode: row.languageCode,
        translationText: row._draftText ?? "",
      });
      if (!res?.ok) {
        throw new Error(res?.message || "Failed to update translation");
      }
      patchRow(row.translationKey, row.languageCode, {
        translationText: res?.data?.translationText ?? row._draftText,
        _draftText: res?.data?.translationText ?? row._draftText,
        updatedAt: res?.data?.updatedAt ?? row.updatedAt,
        _saving: false,
        _error: "",
      });
      setInfo(res?.message || "Updated");
    } catch (e) {
      patchRow(row.translationKey, row.languageCode, {
        _saving: false,
        _error: e?.response?.data?.message || e?.message || "Failed to update",
      });
    }
  };

  const onDeleteRow = async (row) => {
    const confirmed = window.confirm(
      `Delete translation?\n\n${row.translationKey} [${row.languageCode}]`
    );
    if (!confirmed) return;

    patchRow(row.translationKey, row.languageCode, { _deleting: true, _error: "" });
    setInfo("");
    try {
      const res = await deleteTranslation({
        translationKey: row.translationKey,
        languageCode: row.languageCode,
      });
      if (!res?.ok) {
        throw new Error(res?.message || "Failed to delete translation");
      }
      setRows((prev) =>
        prev.filter(
          (it) =>
            !(it.translationKey === row.translationKey && it.languageCode === row.languageCode)
        )
      );
      setInfo(res?.message || "Deleted");
    } catch (e) {
      patchRow(row.translationKey, row.languageCode, {
        _deleting: false,
        _error: e?.response?.data?.message || e?.message || "Failed to delete",
      });
    }
  };

  return (
    <div className="p-3">
      <div className="card mb-3">
        <div className="card-body py-2 px-3">
          <div className="d-flex justify-content-between align-items-center gap-2">
            <div>
              <h5 className="card-title mb-0">Create Translation</h5>
              <div className="text-muted" style={{ fontSize: 12 }}>
                Add new translation rows
              </div>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={() => setShowCreateForm((prev) => !prev)}
            >
              {showCreateForm ? "Hide" : "Show"}
            </button>
          </div>
        </div>
        {showCreateForm && (
          <div className="card-body pt-0 px-3 pb-3">
          <form onSubmit={onCreate}>
            <div className="row g-3">
              <div className="col-md-5">
                <label className="form-label">Translation Key</label>
                <input
                  className="form-control form-control-sm"
                  value={createForm.translationKey}
                  onChange={(e) =>
                    setCreateForm((prev) => ({ ...prev, translationKey: e.target.value }))
                  }
                  placeholder="emission.category.1.name"
                  required
                />
              </div>
              <div className="col-md-2">
                <label className="form-label">Language</label>
                <select
                  className="form-select form-select-sm"
                  value={createLanguageSelectValue}
                  onChange={(e) => {
                    const value = e.target.value;
                    setCreateForm((prev) => ({
                      ...prev,
                      languageCode:
                        value === CUSTOM_LANGUAGE_OPTION ? "" : value,
                    }));
                  }}
                  required
                >
                  {LANGUAGE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                  <option value={CUSTOM_LANGUAGE_OPTION}>Custom...</option>
                </select>
                {createLanguageSelectValue === CUSTOM_LANGUAGE_OPTION && (
                  <input
                    className="form-control form-control-sm mt-2"
                    value={createForm.languageCode}
                    onChange={(e) =>
                      setCreateForm((prev) => ({ ...prev, languageCode: e.target.value }))
                    }
                    placeholder="Enter language code (e.g. pt-BR)"
                    required
                  />
                )}
              </div>
              <div className="col-12">
                <label className="form-label">Translation Text</label>
                <AutosizeTextarea
                  className="form-control form-control-sm"
                  value={createForm.translationText}
                  onChange={(e) =>
                    setCreateForm((prev) => ({ ...prev, translationText: e.target.value }))
                  }
                  placeholder="Translated value"
                  minRows={2}
                  required
                />
              </div>
            </div>
            <div className="mt-2 d-flex gap-2">
              <button className="btn btn-sm btn-primary" type="submit" disabled={submitting}>
                {submitting ? "Creating..." : "Create"}
              </button>
              <button
                className="btn btn-sm btn-outline-secondary"
                type="button"
                onClick={() => setCreateForm(emptyCreateForm())}
                disabled={submitting}
              >
                Reset
              </button>
            </div>
          </form>
        </div>
        )}
      </div>

      <div className="card">
        <div className="card-body py-3 px-3">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <div className="d-flex align-items-center gap-2">
              <h5 className="card-title mb-0">Translations</h5>
              <span className="badge bg-light text-dark border">{rows.length}</span>
            </div>
            <div className="d-flex gap-2">
              <button
                className="btn btn-outline-primary btn-sm"
                type="button"
                onClick={() => loadTranslations()}
                disabled={loading}
              >
                Refresh
              </button>
            </div>
          </div>

          <div className="row g-2 mb-2">
            <div className="col-md-4">
              <input
                className="form-control form-control-sm"
                placeholder="Search key or text"
                value={filters.search}
                onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
              />
            </div>
            <div className="col-md-4">
              <input
                className="form-control form-control-sm"
                placeholder="Filter by translation key"
                value={filters.translationKey}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, translationKey: e.target.value }))
                }
              />
            </div>
            <div className="col-md-2">
              <select
                className="form-select form-select-sm"
                value={filterLanguageSelectValue}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === CUSTOM_LANGUAGE_OPTION) {
                    setFilterLanguageMode("custom");
                    setFilters((prev) => ({ ...prev, languageCode: "" }));
                    return;
                  }
                  setFilterLanguageMode("select");
                  setFilters((prev) => ({ ...prev, languageCode: value }));
                }}
              >
                <option value="">All languages</option>
                {LANGUAGE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
                <option value={CUSTOM_LANGUAGE_OPTION}>Custom...</option>
              </select>
              {(filterLanguageMode === "custom" ||
                filterLanguageSelectValue === CUSTOM_LANGUAGE_OPTION) && (
                  <input
                    className="form-control form-control-sm mt-2"
                    placeholder="Custom language code"
                    value={filters.languageCode}
                    onChange={(e) =>
                      setFilters((prev) => ({ ...prev, languageCode: e.target.value }))
                    }
                  />
                )}
            </div>
            <div className="col-md-2 d-grid">
              <button
                className="btn btn-sm btn-primary"
                type="button"
                onClick={() => loadTranslations()}
                disabled={loading}
              >
                Apply
              </button>
            </div>
          </div>

          {error && <div className="alert alert-danger py-1 px-2 mb-2" style={{ fontSize: 13 }}>{error}</div>}
          {info && <div className="alert alert-success py-1 px-2 mb-2" style={{ fontSize: 13 }}>{info}</div>}

          {loading ? (
            <div>Loading...</div>
          ) : (
            <div className="table-responsive" style={{ maxHeight: "68vh", overflow: "auto" }}>
              <table className="table table-sm table-bordered align-middle mb-0" style={{ fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={{ minWidth: 260, position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>Translation Key</th>
                    <th style={{ width: 90, position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>Lang</th>
                    <th style={{ position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>Translation Text</th>
                    <th style={{ width: 170, position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>Updated</th>
                    <th style={{ width: 150, position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={5} className="text-center text-muted py-4">
                        No translations found.
                      </td>
                    </tr>
                  )}

                  {rows.map((row) => {
                    const unchanged = (row._draftText ?? "") === (row.translationText ?? "");
                    return (
                      <tr key={`${row.translationKey}::${row.languageCode}`}>
                        <td style={{ verticalAlign: "top" }}>
                          <code
                            title={row.translationKey}
                            style={{
                              display: "inline-block",
                              maxWidth: 360,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              fontSize: 12,
                            }}
                          >
                            {row.translationKey}
                          </code>
                        </td>
                        <td style={{ verticalAlign: "top", whiteSpace: "nowrap", fontSize: 12 }}>
                          {row.languageCode}
                        </td>
                        <td style={{ verticalAlign: "top" }}>
                          <AutosizeTextarea
                            className="form-control form-control-sm"
                            value={row._draftText ?? ""}
                            onChange={(e) =>
                              patchRow(row.translationKey, row.languageCode, {
                                _draftText: e.target.value,
                              })
                            }
                            disabled={row._saving || row._deleting}
                            minRows={1}
                            style={{ minWidth: 220, lineHeight: "18px", fontSize: 12 }}
                          />
                          {row._error && (
                            <div className="text-danger mt-1" style={{ fontSize: 12 }}>
                              {row._error}
                            </div>
                          )}
                        </td>
                        <td style={{ fontSize: 11, verticalAlign: "top", whiteSpace: "nowrap" }}>
                          {row.updatedAt ? new Date(row.updatedAt).toLocaleString() : "-"}
                        </td>
                        <td style={{ verticalAlign: "top" }}>
                          <div className="d-flex gap-1">
                            <button
                              type="button"
                              className="btn btn-sm btn-primary py-0 px-2"
                              disabled={row._saving || row._deleting || unchanged}
                              onClick={() => onSaveRow(row)}
                            >
                              {row._saving ? "Saving..." : "Save"}
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger py-0 px-2"
                              disabled={row._saving || row._deleting}
                              onClick={() => onDeleteRow(row)}
                            >
                              {row._deleting ? "Deleting..." : "Delete"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
